import { currentEnv } from './env';
import { SENDER } from './mail';
import type { AuthProblem } from './backend';

/*
 * WHERE EACH COPY ACTUALLY LIVES — the addresses people are given, not the
 * hosting provider's own.
 *
 * These were `flightdeck-vsnl.web.app` and `flightdeck-uat-584f7.web.app`
 * until 18 August 2026, which is where the site was hosted before it had a
 * name of its own. Everything else moved and this did not, so the invitation a
 * person copied and sent to a colleague told them to open a Firebase URL —
 * found by Vinnie, who had to read it in the message he was about to send.
 *
 * The site's own configuration already said `app.flightdeck.build`: the check
 * in scripts/test-auth-settings.mjs asserts it as Supabase's `site_url`, and
 * every emailed link is built from that. So the emails were right and only the
 * words a person copies were wrong — which is exactly the kind of thing no
 * test looked at, because nothing on the machine ever reads them.
 */

/** The real board. */
export const LIVE_ORIGIN = 'https://app.flightdeck.build';

/** The permanent test copy, where somebody can explore without touching real work. */
export const UAT_ORIGIN = 'https://uat.flightdeck.build';

/**
 * Where a sign-in link should land the person who clicks it.
 *
 * THE PLACE THAT SENT IT. Invite somebody from the test copy and they arrive at
 * the test copy; invite them from the live board and they arrive at the live
 * board. The button does what the screen you are looking at implies.
 *
 * IT DID NOT USED TO. Every link pointed at the live board wherever it was sent
 * from, on the reasoning that somebody invited to a project wants the project
 * rather than whichever copy the inviter happened to have open. That is true of
 * a colleague joining the team and false of a tester, who is being asked to
 * explore precisely because it is NOT real work — and there was no way to
 * invite one.
 *
 * A BRANCH PREVIEW STILL SENDS TO THE TEST COPY. Its address changes every time
 * one is created, so it can never be trusted in advance and a link pointing at
 * it would be refused. The permanent test copy is the nearest place that is
 * both allowed and not live.
 *
 * WHAT THIS DEPENDS ON. A link may only point somewhere the project has been
 * told to trust, or it is refused before it is sent. Each environment keeps its
 * own list — Supabase calls them redirect URLs — so an address added here has
 * to be added there too, in the project that sends it.
 */
export function signInTarget(): string {
  switch (currentEnv()) {
    // On a laptop there is no live board to reach, and localhost is trusted.
    case 'dev':
      return window.location.origin;
    case 'live':
      return LIVE_ORIGIN;
    default:
      return UAT_ORIGIN;
  }
}

/**
 * Whether the person sending it should be told where it goes.
 *
 * Not because it is wrong — it is now the right destination — but because
 * "you are inviting somebody to the TEST copy" is worth saying out loud before
 * a real colleague is sent somewhere their work will not count.
 */
export function targetIsElsewhere(): boolean {
  return currentEnv() === 'uat';
}

/**
 * AN INVITATION IN WORDS, FOR THE PERSON DOING THE INVITING TO SEND.
 *
 * WHY THIS EXISTS AT ALL. The email Firebase sends when you press "Send
 * sign-in link" cannot be reworded. Read from the project's own configuration,
 * it offers four templates — reset password, verify email, change email, revert
 * second factor — and NONE for a sign-in link. It is not a setting somebody has
 * hidden; there is nothing there to change. So a person you have invited
 * receives an unexplained message ending "If you did not request this link, you
 * can safely ignore this email" — which tells somebody who has just been added
 * to ignore the only thing that lets them in.
 *
 * Rather than pretend that is fixable here, this hands the sender a message
 * they send themselves, from their own address, to somebody who knows them.
 * The recipient then asks the board for their own link — and the Firebase email
 * arrives EXPECTED, which is the whole of the harm removed. What it does not
 * fix is the wording, and that stays on the roadmap as the launch blocker it is.
 */
export function invitationText(opts: {
  from: string;
  /**
   * What they have been given access to. A single name, OR the LIST of boards they
   * can actually open — so the note names their real reach, not whichever board
   * happened to be open when it was copied (#3). An empty list falls back to the
   * single-name form the caller passes instead.
   */
  boardName: string | string[];
  target: string;
  isTest: boolean;
}): string {
  const { from, boardName, target, isTest } = opts;
  // One name, or a worded list — "A", "A and B", "A, B and C".
  const named = Array.isArray(boardName) ? listWords(boardName) : boardName;
  const what = isTest
    ? `a test copy of ${named}, so nothing you do there affects real work`
    : named;
  return [
    `I have given you access to ${what}.`,
    '',
    `To get in, open ${target} and enter this email address.`,
    '',
    `You will be sent a sign-in link from ${SENDER}. Open it, then press`,
    '"Sign me in" on the page. There is no password to create or remember.',
    '',
    `— ${from}`,
  ].join('\n');
}

/** A few names as a reader would say them: "A", "A and B", "A, B and C". */
function listWords(names: string[]): string {
  if (names.length === 0) return 'the board';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The one failure worth explaining rather than printing.
 *
 * An untrusted address means the link was refused before it was sent. Shown as
 * a raw code it reads as a fault in the board; it is one setting on one screen,
 * and saying which turns a dead end into a task.
 *
 * ASKED BY REASON, NOT BY CODE. This used to match `auth/unauthorized-continue-uri`
 * — Firebase's own string — which would still have compiled and still have run
 * after a move to another provider, and would simply have stopped recognising
 * the one failure it exists to explain. src/backend/auth.ts turns whatever the
 * provider says into one of these.
 */
export function inviteProblem(e: { reason?: AuthProblem; code?: string }, target: string): string {
  switch (e.reason) {
    case 'untrusted-address':
      return `${new URL(target).hostname} is not on the list of addresses this project may send people to, so the link was refused. Add it to the project's redirect URLs.`;
    case 'bad-email':
      return 'That does not look like an email address.';
    case 'too-many':
      return 'Too many links sent just now. Wait a minute and try again.';
    default:
      return e.code || 'unknown error';
  }
}
